import { computed, Injectable, signal } from "@angular/core";
import { jwtDecode } from "jwt-decode";

import { JwtPayload } from "@api/models/auth.model";
import { CreditUnionWithRole, CurrentCreditUnion } from "@api/models/credit-union-portal.model";
import { UserProfile } from "@api/models/user-profile-portal.model";

@Injectable({ providedIn: 'root' })
export class UserState {
    readonly userInfo = signal<JwtPayload | null>(null);
    readonly userProfileData = signal<UserProfile | null>(null);
    readonly creditUnion = signal<CurrentCreditUnion | null>(null);
    readonly creditUnions = signal<CreditUnionWithRole[] | null>(null);

    readonly isPortalAdmin = computed(() => this.userInfo()?.portalAdmin ?? false);
    readonly userRole = computed(() => this.userProfileData()?.role ?? '');
    readonly tier = computed(() => this.userProfileData()?.tier ?? '');
    readonly isLeadLenderEligible = computed(() => this.userProfileData()?.leadLenderEligibility ?? false);
    readonly lendingPreferencesUpdatedAt = computed(() => {
        const profile = this.userProfileData();
        return profile ? profile.lendingPreferencesUpdatedAt || null : undefined;
    });

    readonly creditUnionId = computed(() => this.creditUnion()?.id ?? '');
    readonly creditUnionName = computed(() => this.creditUnion()?.creditUnionName ?? '');
    readonly charterNumber = computed(() => this.creditUnion()?.charterNumber ?? '');
    readonly hasMultipleCreditUnions = computed(() => this.isPortalAdmin() || (this.creditUnions()?.length ?? 0) > 1);
    readonly selectedCreditUnionAccess = computed(() => {
        const creditUnionId = this.creditUnionId();
        return this.creditUnions()?.find(creditUnion => creditUnion.id === creditUnionId) ?? null;
    });
    readonly isCreditUnionAdmin = computed(() => this.isPortalAdmin() || this.selectedCreditUnionAccess()?.role === 'CREDIT_UNION_ADMIN');
    readonly canSubmitInterest = computed(() => this.isCreditUnionAdmin() || this.selectedCreditUnionAccess()?.canSubmitInterest === true);
    readonly availableReports = computed(() => this.selectedCreditUnionAccess()?.availableReports ?? []);
    readonly isCreditUnionAccessLoaded = computed(() => !!this.creditUnionId() && this.creditUnions() !== null);

    setFromToken(token: string): void {
        this.userInfo.set(jwtDecode<JwtPayload>(token));
    }

    setProfile(profile: UserProfile): void {
        this.userProfileData.set(profile);
    }

    setCreditUnion(creditUnion: CurrentCreditUnion): void {
        this.creditUnion.set(creditUnion);
    }

    setCreditUnions(creditUnions: CreditUnionWithRole[]): void {
        this.creditUnions.set(creditUnions);
    }

    clear(): void {
        this.userInfo.set(null);
        this.userProfileData.set(null);
        this.creditUnion.set(null);
        this.creditUnions.set(null);
    }
}
